import React, { useState, useCallback, useMemo } from 'react';
import { Buttons } from 'lidya-common-components';
import { Trans, useTranslation } from 'gatsby-plugin-react-i18next';
import I18nModalLimitedOperations from '../i18nModalLimitedOperations';
import { GetExternalLinkButton } from '../../common/externalLinks/external.link.button';
import { useGlobalData } from '../../../providers/globalDataProvider/globalDataProvider.useGlobalData';
import { getLinkByComponentName } from '../../../hooks/useNavigationLinks';
import { COUNTRY_LANGUAGE_LIST, COMPONENTS } from '../../common/webSiteLanguages';
import { HomeButton } from './customActionButtons/homeButton';
import { SignUpModalButton } from './customActionButtons/signUpModalButton';
import { GetLoanNowButton } from './customActionButtons/getLoanNowButton';

const ACTION_BUTTON_TYPES = {
  HOME: 'home',
  SIGN_UP: 'sign_up',
  CHANGE: 'change',
  GET_LOAN: 'get_loan',
  IMMEDIATE_LOAN: 'immediate_loan',
};

function ModalLimitedOperations() {
  const [showModal, setShowModal] = useState(false);
  const toggleModal = useCallback(
    () => setShowModal((oldValue) => !oldValue),
    [],
  );
  return (
    <>
      {showModal && (
      <I18nModalLimitedOperations
        show={showModal}
        onClose={() => toggleModal()}
      />
      )}
      <Buttons.Button
        type={Buttons.BUTTON_TYPES.PRIMARY}
        onClick={() => toggleModal()}
      >
        <Trans>web.modal.language.limited.home.label</Trans>
      </Buttons.Button>
    </>
  );
}

const getCustomAction = ({
  actionType, t, country, language, CustomActionArea,
}) => {
  const customAction = {
    CustomActionComponent: CustomActionArea,
    customActionText: CustomActionArea ? '' : t('web.header.support.text'),
  };

  if (CustomActionArea || !actionType) return customAction;
  // let openSignUp = false;
  // if (typeof window !== 'undefined') {
  //   openSignUp = sessionStorage.getItem('openSignUp') === 'true';
  //   sessionStorage.removeItem('openSignUp');
  // }
  switch (actionType) {
    case ACTION_BUTTON_TYPES.HOME:
      customAction.CustomActionComponent = function () {
        const link = getLinkByComponentName({
          componentName: COMPONENTS.HOME,
          country,
        });
        const slug = link[`slug_${language}`];
        const { new_tab: newTab } = link;
        return (
          <HomeButton slug={slug} newTab={newTab} />
        );
      };
      customAction.customActionText = t('web.404.support.text');
      break;

    case ACTION_BUTTON_TYPES.SIGN_UP:
      customAction.CustomActionComponent = function () {
        return (
          <SignUpModalButton />
        );
      };
      break;
    case ACTION_BUTTON_TYPES.GET_LOAN:
      customAction.CustomActionComponent = function () {
        return (
          <div className="a-navigation__starterButton -desktop">
            <GetExternalLinkButton id="GetLoanHero" />
          </div>
        );
      };
      break;
    case ACTION_BUTTON_TYPES.CHANGE:
      customAction.CustomActionComponent = function () {
        return <ModalLimitedOperations />;
      };
      break;

    case ACTION_BUTTON_TYPES.IMMEDIATE_LOAN:
      customAction.customActionText = <Trans>web.header.support.text.loan</Trans>;
      customAction.CustomActionComponent = function () {
        return <GetLoanNowButton />;
      };
      break;

    default:
      customAction.customActionText = undefined;
      break;
  }
  return customAction;
};

export const useCustomAction = ({ title, CustomActionArea }) => {
  const { language, country, activeComponent } = useGlobalData();
  const { t } = useTranslation();

  const { hasOperations, canGetLoan, immediateLoan } = COUNTRY_LANGUAGE_LIST[country];

  let actionType;

  if (title.includes(COMPONENTS[404])) {
    actionType = ACTION_BUTTON_TYPES.HOME;
  } else if (activeComponent.includes(COMPONENTS.HOME)) {
    if (hasOperations && canGetLoan) {
      actionType = ACTION_BUTTON_TYPES.GET_LOAN;
    } else {
      actionType = ACTION_BUTTON_TYPES.SIGN_UP;
    }
    if (!hasOperations) {
      actionType = ACTION_BUTTON_TYPES.CHANGE;
    }
    if (immediateLoan) {
      actionType = ACTION_BUTTON_TYPES.IMMEDIATE_LOAN;
    }
  }

  const customActionObject = useMemo(
    () => getCustomAction({
      actionType, country, t, language, CustomActionArea,
    }),
    [CustomActionArea, actionType, country, language, t],
  );

  return { ...customActionObject };
};

export default useCustomAction;
